import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModuleOrientation } from '../../../../@types/enums';
import { useViewerStore } from '../../../../store/store';
import {
    ClearDebugDraw,
    DebugDrawManager,
    getOneDrawDebuggerOptions,
    setOneDrawDebuggerOptions,
    type ShadeWorkerOptions,
} from './Controllers/DebugDraw';
import { labelMonthArr } from './Sun-Vector-Path/LoadSun';
import { refreshObstructionCornerDepthRays } from './Controllers/ObstructionControl';
import { addShadeEnabledRoofIds, ensureRoofShadeGeometry } from './Controllers/RefreshShade';
import { RoofMesh, type ShadeData } from '../../../../@types/types';

const MODULE_FILL_SIM_EVENT = 'module-fill-simulation';
const MODULE_FILL_DEBUG_INFO_EVENT = 'module-fill-debugger-info';
const MODULE_PLACEMENT_STATUS_DEBUG_EVENT = 'module-placement-status-debug';

type FillSimulationEvent = {
    phase: 'start' | 'step' | 'complete';
    roofFaceId?: number | string;
    roofNumber?: number | string;
    total?: number;
    delayMs?: number;
    index?: number;
    x?: number;
    z?: number;
    dir?: string;
    moduleId?: string;
    placed?: number;
    obstructionIds?: string[];
    obstructionHitCount?: number;
};

type FillDebuggerInfoEvent = {
    roofFaceId?: number | string;
    allowedAreasCount?: number;
    allowedAreasSummary?: Array<{
        idx: number;
        points: number;
        minX: number;
        maxX: number;
        minZ: number;
        maxZ: number;
        vertices: Array<{ x: number; z: number }>;
    }>;
};

type ModulePlacementStatusDebugEvent = {
    status?: string;
    roofFaceId?: number | string | null;
    timestamp?: number;
};

type DebuggerTab = 'fill-debugger' | 'shade-worker';

type ShadeDebugRoof = {
    roofFaceId: string | undefined;
    roofName: number;
    // shadePosArr: Array<{ x: number; y: number; z: number }>;
};

type ShadeDebugPoint = {
    idx: number;
    label: number;
};

function filterSunVectorRayIntersects(rays: ShadeData[], options: ShadeWorkerOptions): ShadeData[] {
    if (!options.drawSunVector || !options.sunVectorRoofFaceId) return [];
    const roofFaceId = String(options.sunVectorRoofFaceId);
    return rays
        .filter((ray) => {
            if (String(ray.roofFaceId) !== roofFaceId) return false;
            if (ray.ptIdx !== options.sunVectorShadePointIdx) return false;
            if (ray.monthIdx !== options.sunVectorMonth) return false;
            if (ray.dayIdx !== options.sunVectorDay) return false;
            if (!options.sunVectorFullDay && ray.hourIdx !== options.sunVectorHour) return false;
            return true;
        })
        .sort((a, b) => a.hourIdx - b.hourIdx);
}

const SHADE_RENDER_MESH_KEYS: (keyof ShadeWorkerOptions)[] = [
    'renderRoof',
    'renderWall',
    'renderTree',
    'renderObstruction',
    'renderRackingModule',
    'renderMap',
];

const ALLOWED_AREAS_DEBUG_GROUP = 'allowedAreasDebugOverlay';
const DEFAULT_ALLOWED_AREAS_DEBUG_GROUP = 'defaultAllowedAreasDebugOverlay';
const DEFAULT_FILL_CORNER_DEBUG_GROUP = 'defaultFillCornerDebug';

function applyFillDebugVisibility(sceneViewer: any, options: ReturnType<typeof getOneDrawDebuggerOptions>['fillDebugger']) {
    if (!sceneViewer) return;
    const roofs = sceneViewer?.roofMeshArr || [];
    roofs.forEach((roofMesh: any) => {
        roofMesh?.children?.forEach((child: any) => {
            if (child?.name === DEFAULT_FILL_CORNER_DEBUG_GROUP) {
                child.visible = !!options.showStartCorner;
                return;
            }

            const debugOverlayGroup = child?.userData?.debugOverlayGroup;
            if (debugOverlayGroup === ALLOWED_AREAS_DEBUG_GROUP) {
                child.visible = !!options.showAutofillAllowedAreas;
                return;
            }
            if (debugOverlayGroup === DEFAULT_ALLOWED_AREAS_DEBUG_GROUP) {
                child.visible = !!options.showDefaultAllowedAreas;
            }
        });
    });
    if (typeof sceneViewer?.toggleRoofEdgeTypeLabel === 'function') {
        sceneViewer.toggleRoofEdgeTypeLabel(!!options.showRoofEdgeTypes);
    }
    DebugDrawManager.execute(sceneViewer, 'toggleTreeCylinders', { show: !!options.showTreeCylinders });

    if (options.showBoundMeshBox) {
        const boundMesh = sceneViewer?.boundGroup?.children?.[0];
        if (boundMesh) DebugDrawManager.execute(sceneViewer, 'drawBoundMeshBox', { boundMesh, group: 'bound-mesh' });
    } else {
        ClearDebugDraw(sceneViewer, 'bound-mesh');
    }

    if (!options.showObstructionCenters) {
        ClearDebugDraw(sceneViewer, 'obstruction-centers');
    } else {
        DebugDrawManager.execute(sceneViewer, 'drawObstructionCentersFromScene', { group: 'obstruction-centers' });
    }
    if (!options.showObstructionNodes) {
        ClearDebugDraw(sceneViewer, 'obstruction-nodes');
    } else {
        DebugDrawManager.execute(sceneViewer, 'drawObstructionNodesFromScene', { group: 'obstruction-nodes' });
    }
    if (!options.showRoofNodes) {
        ClearDebugDraw(sceneViewer, 'roof-nodes');
    } else {
        DebugDrawManager.execute(sceneViewer, 'drawRoofNodesFromScene', { group: 'roof-nodes' });
    }
    if (!options.showModuleCollisionObb) {
        ClearDebugDraw(sceneViewer, 'module-collision');
    }
    if (!options.showWallObb) {
        ClearDebugDraw(sceneViewer, 'wall-obb');
    } else {
        DebugDrawManager.execute(sceneViewer, 'drawWallObbFromScene', { group: 'wall-obb' });
    }
    if (!options.showObstructionCornerDepthRays) {
        ClearDebugDraw(sceneViewer, 'obstruction-corner-depth-rays');
    } else {
        refreshObstructionCornerDepthRays(sceneViewer);
    }
    if (!options.showObstructionRayArrows) {
        ClearDebugDraw(sceneViewer, 'obstruction-ray-arrows');
    }
}

const OneDrawDebuggerGUI: React.FC = () => {
    const sceneViewer = useViewerStore((state) => state.sceneViewer);
    const selRoofFaceArr = useViewerStore((state) => state.selRoofFaceArr);
    const refreshingShade = useViewerStore((state) => state.refreshingShade);
    const rayIntersectArr = useViewerStore((state) => state.rayIntersectArr);

    const [activeTab, setActiveTab] = React.useState<DebuggerTab>('fill-debugger');
    const [isVisible, setIsVisible] = React.useState(true);
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [panelPosition, setPanelPosition] = React.useState({ x: 120, y: 110 });
    const [fillType, setFillType] = React.useState<ModuleOrientation>(ModuleOrientation.Both);
    const [fillDebugStatus, setFillDebugStatus] = React.useState('Idle');
    const [fillDebugTotal, setFillDebugTotal] = React.useState(0);
    const [fillDebugPlaced, setFillDebugPlaced] = React.useState(0);
    const [fillDebugLastStep, setFillDebugLastStep] = React.useState<FillSimulationEvent | null>(null);
    const [fillDebugLog, setFillDebugLog] = React.useState<string[]>([]);
    const [allowedAreasInfo, setAllowedAreasInfo] = React.useState<FillDebuggerInfoEvent | null>(null);
    const [fillOptions, setFillOptions] = React.useState(getOneDrawDebuggerOptions().fillDebugger);
    const [shadeOptions, setShadeOptions] = React.useState(getOneDrawDebuggerOptions().shadeWorker);

    const dragStateRef = React.useRef({ dragging: false, offsetX: 0, offsetY: 0, pointerId: -1 });

    React.useEffect(() => {
        if (selRoofFaceArr?.length > 0) setIsVisible(true);
    }, [selRoofFaceArr]);

    React.useEffect(() => {
        applyFillDebugVisibility(sceneViewer, fillOptions);
    }, [sceneViewer, fillOptions]);

    React.useEffect(() => {
        if (!sceneViewer) return;
        const show = !!shadeOptions.showSunPathAnalemma;
        sceneViewer.hideObject('SunVector', show);
        sceneViewer.showSunPathLine(show);
    }, [sceneViewer, shadeOptions.showSunPathAnalemma]);

    React.useEffect(() => {
        const onSimulationEvent = (event: Event) => {
            const customEvent = event as CustomEvent<FillSimulationEvent>;
            const detail = customEvent.detail || { phase: 'start' };
            setIsVisible(true);

            if (detail.phase === 'start') {
                setFillDebugStatus('Running');
                setFillDebugTotal(detail.total || 0);
                setFillDebugPlaced(0);
                setFillDebugLastStep(null);
                setFillDebugLog([
                    `Roof: ${detail.roofNumber ?? detail.roofFaceId ?? '-'} | Modules: ${detail.total ?? 0} | ObstructionIds: ${(detail.obstructionIds || []).join(',') || '-'} | ObstructionHits: ${detail.obstructionHitCount ?? 0}`,
                ]);
                return;
            }

            if (detail.phase === 'step') {
                setFillDebugPlaced(detail.index || 0);
                setFillDebugLastStep(detail);
                return;
            }

            setFillDebugStatus('Completed');
            setFillDebugPlaced(detail.placed ?? 0);
            setFillDebugLog((prev) => [
                ...prev,
                `Roof: ${detail.roofNumber ?? detail.roofFaceId ?? '-'} | Modules: ${detail.placed ?? detail.total ?? 0} | ObstructionIds: ${(detail.obstructionIds || []).join(',') || '-'} | ObstructionHits: ${detail.obstructionHitCount ?? 0}`,
            ].slice(-14));
        };

        const onDebuggerInfoEvent = (event: Event) => {
            const customEvent = event as CustomEvent<FillDebuggerInfoEvent>;
            const detail = customEvent.detail || null;
            if (!detail) return;
            setAllowedAreasInfo(detail);
        };

        const onModulePlacementStatusEvent = (event: Event) => {
            const customEvent = event as CustomEvent<ModulePlacementStatusDebugEvent>;
            const detail = customEvent.detail || {};
            const options = getOneDrawDebuggerOptions().fillDebugger;
            if (!options.logModulePlacementStatus) return;
            const status = detail.status || '-';
            const roofId = detail.roofFaceId ?? '-';
            setFillDebugLog((prev) => [
                ...prev,
                `Placement: ${status} | Roof: ${roofId}`,
            ].slice(-14));
        };

        window.addEventListener(MODULE_FILL_SIM_EVENT, onSimulationEvent as EventListener);
        window.addEventListener(MODULE_FILL_DEBUG_INFO_EVENT, onDebuggerInfoEvent as EventListener);
        window.addEventListener(MODULE_PLACEMENT_STATUS_DEBUG_EVENT, onModulePlacementStatusEvent as EventListener);

        return () => {
            window.removeEventListener(MODULE_FILL_SIM_EVENT, onSimulationEvent as EventListener);
            window.removeEventListener(MODULE_FILL_DEBUG_INFO_EVENT, onDebuggerInfoEvent as EventListener);
            window.removeEventListener(MODULE_PLACEMENT_STATUS_DEBUG_EVENT, onModulePlacementStatusEvent as EventListener);
        };
    }, []);

    const onHeaderPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement)?.closest('button,select,option,input,label')) return;
        dragStateRef.current.dragging = true;
        dragStateRef.current.offsetX = event.clientX - panelPosition.x;
        dragStateRef.current.offsetY = event.clientY - panelPosition.y;
        dragStateRef.current.pointerId = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onHeaderPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.dragging) return;
        if (dragStateRef.current.pointerId !== event.pointerId) return;
        setPanelPosition({
            x: Math.max(10, event.clientX - dragStateRef.current.offsetX),
            y: Math.max(70, event.clientY - dragStateRef.current.offsetY),
        });
    };

    const onHeaderPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragStateRef.current.pointerId === event.pointerId) {
            dragStateRef.current.dragging = false;
            dragStateRef.current.pointerId = -1;
        }
    };

    const onToggleFillOption = (key: keyof typeof fillOptions, value: boolean) => {
        const nextOptions = { ...fillOptions, [key]: value };
        setFillOptions(nextOptions);
        setOneDrawDebuggerOptions({ fillDebugger: nextOptions });
        applyFillDebugVisibility(sceneViewer, nextOptions);
    };

    const shadeDebugRoofs = (): ShadeDebugRoof[] => {
        return sceneViewer?.roofMeshArr?.map((roof: RoofMesh): ShadeDebugRoof => ({
            roofName: roof.userData.nameOverride || roof.userData.roof_tag_id,
            roofFaceId: roof.roofFaceId,
        })) ?? [];
    };

    const selectedShadeDebugRoof = shadeDebugRoofs().find((roof: ShadeDebugRoof) => roof.roofFaceId === shadeOptions.sunVectorRoofFaceId)
        ?? shadeDebugRoofs()[0];

    const shadeDebugPoints = (): ShadeDebugPoint[] => {
        if (!sceneViewer || !selectedShadeDebugRoof) return [];
        const roofFaceId = selectedShadeDebugRoof.roofFaceId;
        const roofMesh = sceneViewer.roofMeshArr?.find((item: any) => item.roofFaceId === roofFaceId);
        if (roofMesh) {
            return roofMesh.userData.shadeArrForRoof.map((pt: any, idx: number): ShadeDebugPoint => ({
                idx,
                label: idx + 1,
            }));
        }
        return [];
    };
    // const shadeDebugPoints = useMemo((): ShadeDebugPoint[] => {
    //     if (!sceneViewer || !selectedShadeDebugRoof) return [];
    //     const roofFaceId = selectedShadeDebugRoof.roofFaceId;
    //     const face = sceneViewer.roofs?.[0]?.faces?.find((item: any) => item.roofFaceId === roofFaceId);
    //     const roofMesh = sceneViewer.roofMeshArr?.find((item: any) => item.roofFaceId === roofFaceId);
    //     if (face && roofMesh) {
    //         ensureRoofShadeGeometry(face, roofMesh);
    //     }
    //     return face?.shadePosArr?.length
    //         ? face.shadePosArr.map((pt: { x: number; y: number; z: number }, idx: number): ShadeDebugPoint => ({
    //             idx,
    //             label: `Point ${idx}`,
    //         }))
    //         : [];
    // }, [ selectedShadeDebugRoof, refreshingShade]);

    useEffect(() => {
        if (!shadeDebugRoofs().length) return;
        const preferredId = selRoofFaceArr?.[0]?.roofFaceId != null
            ? String(selRoofFaceArr[0].roofFaceId)
            : null;
        const hasCurrent = shadeDebugRoofs().some((roof: ShadeDebugRoof) => roof.roofFaceId === shadeOptions.sunVectorRoofFaceId);
        const nextRoofFaceId = hasCurrent
            ? shadeOptions.sunVectorRoofFaceId
            : (preferredId && shadeDebugRoofs().some((roof: ShadeDebugRoof) => roof.roofFaceId === preferredId)
                ? preferredId
                : shadeDebugRoofs()[0].roofFaceId);
        if (nextRoofFaceId === shadeOptions.sunVectorRoofFaceId) return;
        const nextOptions = { ...shadeOptions, sunVectorRoofFaceId: nextRoofFaceId, sunVectorShadePointIdx: 0 };
        setShadeOptions(nextOptions);
        setOneDrawDebuggerOptions({ shadeWorker: nextOptions });
    }, [shadeDebugRoofs, selRoofFaceArr, shadeOptions.sunVectorRoofFaceId]);

    const prepareSunVectorRoof = (roofFaceId: string) => {
        if (!sceneViewer || !roofFaceId) return;
        const face = sceneViewer.roofs?.[0]?.faces?.find((item: any) => String(item.roofFaceId) === roofFaceId);
        const roofMesh = sceneViewer.roofMeshArr?.find((item: any) => String(item.roofFaceId) === roofFaceId);
        if (face && roofMesh) {
            addShadeEnabledRoofIds([roofFaceId]);
            ensureRoofShadeGeometry(face, roofMesh);
        }
    };

    const refreshShadeIfNeeded = (nextOptions: ShadeWorkerOptions) => {
        if (nextOptions.drawSunVector && nextOptions.sunVectorRoofFaceId) {
            prepareSunVectorRoof(nextOptions.sunVectorRoofFaceId);
        }
        if (sceneViewer?.refreshShade) {
            sceneViewer.refreshShade();
        }
    };

    const onToggleShadeOption = (key: keyof typeof shadeOptions, value: boolean) => {
        const nextOptions = { ...shadeOptions, [key]: value };
        setShadeOptions(nextOptions);
        setOneDrawDebuggerOptions({ shadeWorker: nextOptions });
        if (key !== 'showSunPathAnalemma') {
            refreshShadeIfNeeded(nextOptions);
        }
    };

    const allRenderMeshesOn = SHADE_RENDER_MESH_KEYS.every((key) => shadeOptions[key]);
    const someRenderMeshesOn = SHADE_RENDER_MESH_KEYS.some((key) => shadeOptions[key]);
    const renderMeshesMasterRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (renderMeshesMasterRef.current) {
            renderMeshesMasterRef.current.indeterminate = someRenderMeshesOn && !allRenderMeshesOn;
        }
    }, [someRenderMeshesOn, allRenderMeshesOn]);

    const onToggleAllRenderMeshes = (enabled: boolean) => {
        const nextOptions: ShadeWorkerOptions = {
            ...shadeOptions,
            renderRoof: enabled,
            renderWall: enabled,
            renderTree: enabled,
            renderObstruction: enabled,
            renderRackingModule: enabled,
            renderMap: enabled,
        };
        setShadeOptions(nextOptions);
        setOneDrawDebuggerOptions({ shadeWorker: nextOptions });
        refreshShadeIfNeeded(nextOptions);
    };

    const onSunVectorSettingChange = (patch: Partial<ShadeWorkerOptions>) => {
        const nextOptions = { ...shadeOptions, ...patch };
        setShadeOptions(nextOptions);
        setOneDrawDebuggerOptions({ shadeWorker: nextOptions });
        if (nextOptions.drawSunVector) {
            refreshShadeIfNeeded(nextOptions);
        }
    };

    const filteredSunVectorRays = useMemo(
        () => filterSunVectorRayIntersects(rayIntersectArr ?? [], shadeOptions),
        [
            rayIntersectArr,
            shadeOptions.drawSunVector,
            shadeOptions.sunVectorRoofFaceId,
            shadeOptions.sunVectorShadePointIdx,
            shadeOptions.sunVectorMonth,
            shadeOptions.sunVectorDay,
            shadeOptions.sunVectorHour,
            shadeOptions.sunVectorFullDay,
        ],
    );

    const runFillSimulation = () => {
        sceneViewer?.setRoofModuleDirSimulation(fillType);
    };

    const clearSelectedRoofFill = () => {
        if (!sceneViewer || !selRoofFaceArr?.length) return;
        selRoofFaceArr.forEach((roof) => sceneViewer.setClearFaceModule(roof));
        setFillDebugStatus('Cleared');
        setFillDebugTotal(0);
        setFillDebugPlaced(0);
        setFillDebugLastStep(null);
        setFillDebugLog([]);
        setAllowedAreasInfo(null);
    };

    const clearLogger = () => {
        setFillDebugLog([]);
    };

    if (!isVisible || typeof document === 'undefined') return null;

    return createPortal((
        <div
            className="onedraw-debugger"
            style={{ left: `${panelPosition.x}px`, top: `${panelPosition.y}px` }}
            onMouseDownCapture={(event) => event.stopPropagation()}
        >
            <div
                className="onedraw-debugger__header"
                onPointerDown={onHeaderPointerDown}
                onPointerMove={onHeaderPointerMove}
                onPointerUp={onHeaderPointerUp}
            >
                <span>oneDRAW Debugger GUI</span>
                <div className="onedraw-debugger__actions">
                    <button type="button" onClick={() => setIsCollapsed(!isCollapsed)}>
                        {isCollapsed ? 'Expand' : 'Collapse'}
                    </button>
                </div>
            </div>

            {!isCollapsed && (
                <div className="onedraw-debugger__body">
                    <div className="onedraw-debugger__tabs">
                        <button
                            type="button"
                            className={activeTab === 'fill-debugger' ? 'active' : ''}
                            onClick={() => setActiveTab('fill-debugger')}
                        >
                            Module Fill Debugger
                        </button>
                        <button
                            type="button"
                            className={activeTab === 'shade-worker' ? 'active' : ''}
                            onClick={() => setActiveTab('shade-worker')}
                        >
                            Shade Worker
                        </button>
                    </div>

                    {activeTab === 'fill-debugger' && (
                        <div className="onedraw-debugger__panel">
                            <div className="onedraw-debugger__controls">
                                <label htmlFor="fill-debugger-type">Fill Type:</label>
                                <select
                                    id="fill-debugger-type"
                                    value={fillType}
                                    onChange={(e) => setFillType(e.target.value as ModuleOrientation)}
                                >
                                    <option value={ModuleOrientation.Portrait}>Portrait</option>
                                    <option value={ModuleOrientation.Landscape}>Landscape</option>
                                    <option value={ModuleOrientation.Both}>Both</option>
                                </select>
                                <button type="button" onClick={runFillSimulation}>Simulate</button>
                                <button type="button" onClick={clearSelectedRoofFill}>Clear</button>
                                <button type="button" onClick={clearLogger}>Clear Logger</button>
                            </div>

                            <div className="onedraw-debugger__toggles">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showStartCorner}
                                        onChange={(event) => onToggleFillOption('showStartCorner', event.target.checked)}
                                    />
                                    Show Roof Autofill Start Corner
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showAutofillAllowedAreas}
                                        onChange={(event) => onToggleFillOption('showAutofillAllowedAreas', event.target.checked)}
                                    />
                                    Show Autofill Allowed Areas
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showDefaultAllowedAreas}
                                        onChange={(event) => onToggleFillOption('showDefaultAllowedAreas', event.target.checked)}
                                    />
                                    Show Default Fill Allowed Areas
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showRoofEdgeTypes}
                                        onChange={(event) => onToggleFillOption('showRoofEdgeTypes', event.target.checked)}
                                    />
                                    Show Roof Edge Types
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showBoundMeshBox}
                                        onChange={(event) => onToggleFillOption('showBoundMeshBox', event.target.checked)}
                                    />
                                    Show House Bound Mesh Box
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showObstructionCenters}
                                        onChange={(event) => onToggleFillOption('showObstructionCenters', event.target.checked)}
                                    />
                                    Show Obstruction Centers
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showObstructionNodes}
                                        onChange={(event) => onToggleFillOption('showObstructionNodes', event.target.checked)}
                                    />
                                    Show Obstruction Nodes
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showRoofNodes}
                                        onChange={(event) => onToggleFillOption('showRoofNodes', event.target.checked)}
                                    />
                                    Show Roof Nodes (Aurora Shell Index)
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showTreeCylinders}
                                        onChange={(event) => onToggleFillOption('showTreeCylinders', event.target.checked)}
                                    />
                                    Show Tree Cylinders
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showModuleCollisionObb}
                                        onChange={(event) => onToggleFillOption('showModuleCollisionObb', event.target.checked)}
                                    />
                                    Show Module Collision wiht Obst OBB
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showWallObb}
                                        onChange={(event) => onToggleFillOption('showWallObb', event.target.checked)}
                                    />
                                    Show Wall OBB
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showObstructionCornerDepthRays}
                                        onChange={(event) => onToggleFillOption('showObstructionCornerDepthRays', event.target.checked)}
                                    />
                                    Show Obstruction Corner Depth Rays
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.showObstructionRayArrows}
                                        onChange={(event) => onToggleFillOption('showObstructionRayArrows', event.target.checked)}
                                    />
                                    Show Obstruction Ray Arrows
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fillOptions.logModulePlacementStatus}
                                        onChange={(event) => onToggleFillOption('logModulePlacementStatus', event.target.checked)}
                                    />
                                    Log Module Placement Status
                                </label>
                            </div>

                            <div>Status: {fillDebugStatus}</div>
                            <div>Placed: {fillDebugPlaced}/{fillDebugTotal}</div>
                            <div>Allowed Areas: {allowedAreasInfo?.allowedAreasCount ?? 0}</div>
                            <div>
                                Last: {fillDebugLastStep?.dir ?? '-'} @ ({fillDebugLastStep?.x ?? '-'}, {fillDebugLastStep?.z ?? '-'})
                            </div>
                            {allowedAreasInfo?.allowedAreasSummary?.length ? (
                                <div className="onedraw-debugger__areas">
                                    {allowedAreasInfo.allowedAreasSummary.slice(0, 3).map((area) => (
                                        <div key={`allowed-area-${area.idx}`}>
                                            A{area.idx}: p{area.points} | X[{area.minX},{area.maxX}] Z[{area.minZ},{area.maxZ}]
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            <div><strong>Logger</strong></div>
                            <div className="onedraw-debugger__log">
                                {fillDebugLog.length === 0 ? (
                                    <div>logs will be displayed here</div>
                                ) : (
                                    fillDebugLog.map((entry, idx) => (
                                        <div key={`${entry}-${idx}`}>{entry}</div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'shade-worker' && (
                        <div className="onedraw-debugger__panel">
                            <div className="onedraw-debugger__controls">
                                <strong>Shade Worker</strong>
                                <button type="button" onClick={clearLogger}>Clear Logger</button>
                            </div>
                            <div className="onedraw-debugger__section-box">
                                <div className="onedraw-debugger__section-header onedraw-debugger__section-header--with-control">
                                    <label className="onedraw-debugger__header-check" title="Render all worker meshes">
                                        <input
                                            ref={renderMeshesMasterRef}
                                            type="checkbox"
                                            checked={allRenderMeshesOn}
                                            onChange={(event) => onToggleAllRenderMeshes(event.target.checked)}
                                        />
                                    </label>
                                    <span>Render Worker Meshes</span>
                                </div>
                                <div className="onedraw-debugger__section-body">
                                <div className="onedraw-debugger__toggles onedraw-debugger__toggles--row">
                                    <label title="Render roof">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderRoof}
                                            onChange={(event) => onToggleShadeOption('renderRoof', event.target.checked)}
                                        />
                                        Roof
                                    </label>
                                    <label title="Render wall">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderWall}
                                            onChange={(event) => onToggleShadeOption('renderWall', event.target.checked)}
                                        />
                                        Wall
                                    </label>
                                    <label title="Render tree">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderTree}
                                            onChange={(event) => onToggleShadeOption('renderTree', event.target.checked)}
                                        />
                                        Tree
                                    </label>
                                    <label title="Render obstruction">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderObstruction}
                                            onChange={(event) => onToggleShadeOption('renderObstruction', event.target.checked)}
                                        />
                                        Obst
                                    </label>
                                    <label title="Render racking module">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderRackingModule}
                                            onChange={(event) => onToggleShadeOption('renderRackingModule', event.target.checked)}
                                        />
                                        Rack
                                    </label>
                                    <label title="Render terrain map">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.renderMap}
                                            onChange={(event) => onToggleShadeOption('renderMap', event.target.checked)}
                                        />
                                        Map
                                    </label>
                                </div>
                                </div>
                            </div>
                            <div className="onedraw-debugger__section-box">
                                <div className="onedraw-debugger__section-body">
                                    <label title="Show sun path, analemma, and sun sphere">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.showSunPathAnalemma}
                                            onChange={(event) => onToggleShadeOption('showSunPathAnalemma', event.target.checked)}
                                        />
                                        Show Sun Path Analemma and Sun
                                    </label>
                                </div>
                            </div>
                            <div className="onedraw-debugger__section-box">
                                <div className="onedraw-debugger__section-header onedraw-debugger__section-header--with-control">
                                    <label className="onedraw-debugger__header-check" title="Draw sun vector">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.drawSunVector}
                                            onChange={(event) => onToggleShadeOption('drawSunVector', event.target.checked)}
                                        />
                                    </label>
                                    <span>Sun Vector Analysis</span>
                                </div>
                                <div className="onedraw-debugger__section-body">
                                <div className="onedraw-debugger__field-row">
                                    <label htmlFor="shade-debug-roof">Roof:</label>
                                    <select
                                        id="shade-debug-roof"
                                        value={selectedShadeDebugRoof?.roofFaceId ?? ''}
                                        disabled={shadeDebugRoofs().length === 0}
                                        onChange={(event) => onSunVectorSettingChange({
                                            sunVectorRoofFaceId: event.target.value,
                                            sunVectorShadePointIdx: 0,
                                        })}
                                    >
                                        {shadeDebugRoofs().length === 0 ? (
                                            <option value="">No roofs</option>
                                        ) : (
                                            shadeDebugRoofs().map((roof: ShadeDebugRoof) => (
                                                <option key={roof.roofFaceId} value={roof.roofFaceId}>
                                                    {roof.roofName || roof.roofFaceId}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <label htmlFor="shade-debug-point">Point:</label>
                                    <select
                                        id="shade-debug-point"
                                        value={shadeOptions.sunVectorShadePointIdx}
                                        disabled={!shadeDebugPoints().length}
                                        onChange={(event) => onSunVectorSettingChange({
                                            sunVectorShadePointIdx: Number(event.target.value),
                                        })}
                                    >
                                        {shadeDebugPoints().length === 0 ? (
                                            <option value={0}>Nil</option>
                                        ) : (
                                            shadeDebugPoints().map((point: ShadeDebugPoint) => (
                                                <option key={`shade-pt-${point.idx}`} value={point.idx}>
                                                    {point.label}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="onedraw-debugger__field-row">
                                    <label htmlFor="shade-debug-month">Month:</label>
                                    <select
                                        id="shade-debug-month"
                                        value={shadeOptions.sunVectorMonth}
                                        onChange={(event) => onSunVectorSettingChange({ sunVectorMonth: Number(event.target.value) })}
                                    >
                                        {labelMonthArr.map((month, monthIdx) => (
                                            <option key={month} value={monthIdx}>{month}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="shade-debug-day">Day:</label>
                                    <select
                                        id="shade-debug-day"
                                        value={shadeOptions.sunVectorDay}
                                        onChange={(event) => onSunVectorSettingChange({ sunVectorDay: Number(event.target.value) })}
                                    >
                                        <option key="day-5" value={5}>6</option>
                                        <option key="day-20" value={20}>21</option>
                                    </select>
                                </div>
                                <div className="onedraw-debugger__field-row onedraw-debugger__field-row--time">
                                    <label htmlFor="shade-debug-hour">Time:</label>
                                    <select
                                        id="shade-debug-hour"
                                        value={shadeOptions.sunVectorHour}
                                        disabled={shadeOptions.sunVectorFullDay}
                                        onChange={(event) => onSunVectorSettingChange({ sunVectorHour: Number(event.target.value) })}
                                    >
                                        {Array.from({ length: 24 }, (_, hourIdx) => (
                                            <option key={`hour-${hourIdx}`} value={hourIdx}>{hourIdx}:00</option>
                                        ))}
                                    </select>
                                    <label className="onedraw-debugger__inline-check" title="Render sun vectors for all hours">
                                        <input
                                            type="checkbox"
                                            checked={shadeOptions.sunVectorFullDay}
                                            onChange={(event) => onSunVectorSettingChange({ sunVectorFullDay: event.target.checked })}
                                        />
                                        Full day
                                    </label>
                                </div>
                                {shadeOptions.drawSunVector && (
                                    <div className="onedraw-debugger__ray-list">
                                        <div className="onedraw-debugger__ray-list-title">Ray intersections</div>
                                        {refreshingShade ? (
                                            <div className="onedraw-debugger__ray-list-empty">Calculating shade…</div>
                                        ) : filteredSunVectorRays.length === 0 ? (
                                            <div className="onedraw-debugger__ray-list-empty">No intersections for selection</div>
                                        ) : (
                                            filteredSunVectorRays.map((ray) => (
                                                <div
                                                    key={`ray-${ray.hourIdx}-${ray.monthIdx}-${ray.dayIdx}-${ray.ptIdx}`}
                                                    className="onedraw-debugger__ray-group"
                                                >
                                                    {shadeOptions.sunVectorFullDay && (
                                                        <div className="onedraw-debugger__ray-group-title">{ray.hourIdx}:00</div>
                                                    )}
                                                    {ray.objects.length === 0 ? (
                                                        <div className="onedraw-debugger__ray-list-empty">No objects</div>
                                                    ) : (
                                                        <ul className="onedraw-debugger__ray-objects">
                                                            {ray.objects.map((obj, objIdx) => (
                                                                <li
                                                                    key={`${obj.id}-${obj.meshType}-${objIdx}`}
                                                                    className={obj.delStatus ? 'onedraw-debugger__ray-object--deleted' : undefined}
                                                                >
                                                                    <span className="onedraw-debugger__ray-object-type">{obj.meshType}</span>
                                                                    <span className="onedraw-debugger__ray-object-id">{String(obj.id)}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                                </div>
                            </div>
                            <div><strong>Logger</strong></div>
                            <div className="onedraw-debugger__log">
                                {fillDebugLog.length === 0 ? (
                                    <div>logs will be displayed here</div>
                                ) : (
                                    fillDebugLog.slice(-8).map((entry, idx) => (
                                        <div key={`shade-worker-log-${entry}-${idx}`}>{entry}</div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    ), document.body);
};

export default OneDrawDebuggerGUI;
